import * as React from 'react';
import Image from 'next/image';
import { cn, customImageLoader } from '@restworld/utils-common';
import { Heading, Text } from '@restworld/ui-ds-base';

export type EmployerCandidate = { firstName: string; lastName: string; image?: string };

export interface NewCandidateNotificationProps {
  message?: string;
  isAllSpontaneous?: boolean;
  candidateCount?: number;
  imageDisplayLimit?: number;
  leftOffset?: number;
  candidates?: EmployerCandidate[];
  wrapperStyle?: string;
  imageWrapperStyle?: string;
}

export function NewCandidateNotification({
  isAllSpontaneous,
  message,
  wrapperStyle,
  imageWrapperStyle,
  candidateCount = 0,
  candidates = [],
  imageDisplayLimit = 3,
  leftOffset = 16,
}: NewCandidateNotificationProps) {
  const moreToShowCount = candidateCount && candidateCount - imageDisplayLimit;
  const hasMoreToShow = !!moreToShowCount && moreToShowCount > 0;
  const textLeftOffset =
    (Math.min(candidateCount, imageDisplayLimit) - (candidateCount === 2 || candidateCount === 3 ? 1 : 0)) *
      leftOffset -
    0;

  return candidates?.length ? (
    <NotificationWrapper isSpontaneous={isAllSpontaneous} twStyle={wrapperStyle}>
      <div className="flex">
        {candidates.slice(0, imageDisplayLimit).map((candidate, i) => {
          return (
            <RoundedWrapper
              key={i}
              isPositioned={i > 0}
              twStyles={[{ left: i === 1 ? 18 : i * leftOffset }, imageWrapperStyle || '']}
            >
              {candidate.image ? (
                <Image
                  src={candidate.image}
                  alt=""
                  width={28}
                  height={28}
                  loader={customImageLoader}
                  className="object-cover object-center rounded-full h-7 w-7"
                />
              ) : (
                <Heading
                  as="span"
                  size={5}
                  weight="semibold"
                  className="h-7 w-7 rounded-full grid place-items-center text-sm uppercase bg-purple-50 text-purple-700"
                >
                  {`${candidate?.firstName ? candidate.firstName[0] : ''}${
                    candidate?.lastName ? candidate.lastName[0] : ''
                  }`}
                </Heading>
              )}
            </RoundedWrapper>
          );
        })}
        {!!hasMoreToShow && (
          <RoundedWrapper isPositioned twStyles={[{ left: imageDisplayLimit * leftOffset }, imageWrapperStyle || '']}>
            <Heading
              as="span"
              size={5}
              weight="semibold"
              className="h-7 w-7 rounded-full grid place-items-center text-sm bg-primary-50 text-primary-700"
            >
              {`+${moreToShowCount}`}
            </Heading>
          </RoundedWrapper>
        )}
      </div>
      {!!message && (
        <Text
          as="span"
          size={2}
          weight="medium"
          color="white"
          style={{ marginLeft: candidateCount > 1 ? textLeftOffset : 0 }}
        >
          {message}
        </Text>
      )}
    </NotificationWrapper>
  ) : null;
}

const RoundedWrapper = ({ twStyles, className, ...props }: any) => (
  <div
    className={cn(
      'h-8 w-8 rounded-full bg-white grid place-items-center',
      ({ isPositioned }) => isPositioned && 'absolute',
      twStyles || '',
      className
    )}
    {...props}
  />
);
const NotificationWrapper = ({ isSpontaneous, twStyle, className, ...props }: any) => (
  <div
    className={cn(
      'bg-purple-500 rounded-full p-1 flex items-center gap-2 w-fit pr-3 min-h-[40px] truncate relative',
      twStyle || '',
      className
    )}
    {...props}
  />
);
